import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { FaUndo } from 'react-icons/fa'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const PhotoStripCapture: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, userPreferences, id, gallery, uploadSessionState, logActivity } =
    useSessionStateStore()
  const { getFilter, filters, getFrame, frames } = useConfigStore()
  const { downloadLocal, cameraSettings } = useSettingsStore()
  const { fetchSubscriptionAndCredits } = useCreditsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number | undefined => {
    if (cameraSettings.captureRatio === 'auto') return undefined
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 4 / 3 // Default fallback
  }

  // Get photo count from selected frame - FIXED: Use actual photo count from frame
  const selectedFrame = getFrame(userPreferences.frameId || frames[0]?._id || '')
  const uniquePhotoTypes = new Set(
    selectedFrame?.drawParams.images
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-')) || []
  )
  const photoCount = uniquePhotoTypes.size || 4 // Use actual count, fallback to 4

  // States - Use gallery state directly instead of local state
  const [countdown, setCountdown] = useState<number>(3)
  const [isCapturing, setIsCapturing] = useState(false)
  const [preparing, setPreparing] = useState(true)
  const [allCapturesComplete, setAllCapturesComplete] = useState(false)
  const [retakingIndex, setRetakingIndex] = useState<number | null>(null)
  const [remainingTime, setRemainingTime] = useState<string>('10:00')

  // Recording states
  const FPS = 30
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)

  const stopRef = useRef<boolean>(false)
  const remainingCapturesRef = useRef<number>(photoCount)
  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)

  // Countdown hooks
  const prepCountdownHook = useCountdown(30)
  const captureCountdownHook = useCountdown(3, { audioCue: true })
  const timeoutCountdown = useCountdown(300) // 5 minutes

  useEffect(() => {
    uploadSessionState()
    initializeCapture()

    return (): void => {
      // FIXED: Proper cleanup to stop countdown timers
      stopRef.current = true
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      timeoutCountdown.stop()

      // Clear any remaining timeouts
      const timeouts = window.setTimeout(() => {}, 0)
      for (let i = 0; i < timeouts; i++) {
        window.clearTimeout(i)
      }
    }
  }, [])

  // Attach recorder to canvas when ready
  useEffect((): (() => void) => {
    const canvas = document.querySelector('canvas')
    const canvasContext = canvas?.getContext('bitmaprenderer')
    if (canvasContext && canvas && canvasDimensions) {
      const newCaptureRecorder = new Recorder(canvasContext as RenderingContext, {
        name: `dreambooth-capture`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          bitrate: 2000000
        },
        duration: Infinity,
        download: true,
        frameRate: FPS
      })
      captureCanvasRecorderRef.current = newCaptureRecorder
    }
    return () => {
      captureCanvasRecorderRef.current?.stop()
    }
  }, [canvasDimensions])

  const initializeCapture = (): void => {
    logActivity('Photostrip capture session started', {
      photoCount,
      frameId: userPreferences.frameId
    })

    // Set up timeout countdown
    timeoutCountdown.setOnTick(() => {
      setRemainingTime(timeoutCountdown.getFormattedRemainingSeconds())
    })

    timeoutCountdown.setOnComplete(() => {
      logActivity('Photostrip capture session timed out')
      handleFinish()
    })

    timeoutCountdown.start()

    // Set up prep countdown
    prepCountdownHook.setOnTick(() => {
      // Prep countdown display
    })

    prepCountdownHook.setOnComplete(() => {
      setPreparing(false)
      startCaptureSequence()
    })

    prepCountdownHook.start()
  }

  const startCaptureSession = (): void => {
    logActivity('Photostrip capture session manually started')
    
    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        Promise.race([
          fetchSubscriptionAndCredits(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
        ]).catch((error) => {
          // If fetch fails or times out, log but don't block session start
          console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
        })
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        uploadSessionState()
      }, 2000)
    }
    
    setPreparing(false)
    startCaptureSequence()
  }

  const startCaptureSequence = async (): Promise<void> => {
    console.log(
      `Starting capture sequence with remaining captures: ${remainingCapturesRef.current}`
    )

    // Ensure we're not exceeding the photo count
    if (remainingCapturesRef.current <= 0) {
      console.log('All photos captured, completing session')
      setAllCapturesComplete(true)
      return
    }

    setIsCapturing(true)
    setCountdown(3)

    // --- Start video recording ---
    let captureUrl: string | undefined = undefined
    if (captureCanvasRecorderRef.current) {
      try {
        await captureCanvasRecorderRef.current.start()
      } catch (error) {
        console.error('Error starting capture recorder', error)
      }
    }

    // Set up capture countdown
    captureCountdownHook.setRemainingSeconds(3)
    captureCountdownHook.setOnTick(() => {
      const remaining = captureCountdownHook.getRemainingSeconds()
      setCountdown(remaining)
    })

    // --- Step the recorder during countdown (in parallel) ---
    let frameCount = 0
    const totalFrames = FPS * 3 // 3 second countdown
    const startTime = performance.now()

    // Record frames during countdown (runs in parallel with countdown)
    const recordingPromise = (async (): Promise<void> => {
      while (
        frameCount < totalFrames &&
        captureCanvasRecorderRef.current &&
        captureCanvasRecorderRef.current.status === RecorderStatus.Recording
      ) {
        const currentTime = performance.now()
        const elapsedTime = currentTime - startTime
        const targetFrameTime = (frameCount * 1000) / FPS
        if (elapsedTime >= targetFrameTime) {
          try {
            await captureCanvasRecorderRef.current.step()
            frameCount++
          } catch (error) {
            console.error('Error stepping capture recorder', error)
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 1))
      }
    })()

    const captureCompletionPromise = new Promise<string | undefined>((resolve) => {
      captureCountdownHook.setOnComplete(() => {
        handleCapture().then((url) => {
          resolve(url)
        })
      })
    })

    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()

    // Wait for both countdown and recording to complete
    const [imageUrl] = await Promise.all([captureCompletionPromise, recordingPromise])

    // --- Stop and save capture recording ---
    if (
      captureCanvasRecorderRef.current &&
      captureCanvasRecorderRef.current.status === RecorderStatus.Recording
    ) {
      try {
        const captureChunks = await captureCanvasRecorderRef.current.stop()
        const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
        captureUrl = URL.createObjectURL(captureBlob)

        // Update the captured item with the recording URL
        if (imageUrl && captureUrl) {
          const currentItems = useSessionStateStore.getState().gallery.capturedItems || []
          const lastItemIndex = currentItems.length - 1

          if (lastItemIndex >= 0 && currentItems[lastItemIndex]) {
            const updatedItems = [...currentItems]
            updatedItems[lastItemIndex] = {
              ...updatedItems[lastItemIndex],
              recordingUrl: captureUrl
            }

            setSessionState({
              gallery: {
                capturedItems: updatedItems
              }
            })
          }
        }
      } catch (error) {
        console.error('Error stopping capture recorder', error)
      }
    }
  }

  const handleCapture = async (): Promise<string | undefined> => {
    try {
      console.log('Starting capture process')
      logActivity('Captured a photo. Processing...')

      setSessionState({
        // Stats tracked by Gallery API
      })

      flashEffect('.flash')
      setIsCapturing(false)

      await new Promise((resolve) => setTimeout(resolve, 200))

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        undefined,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        console.log('Successfully captured image')
        const imageUrl = URL.createObjectURL(imageData)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          logActivity(`Downloaded photo`, {
            type: 'photo',
            filename: `dreambooth-${id}-${Date.now()}.jpg`
          })
        }

        // Only decrement remaining captures if it's not a retake
        if (retakingIndex === null) {
          const captureId = `${id}-photo-${photoCount - remainingCapturesRef.current + 1}-${Date.now()}`

          // Add new captured item to gallery
          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          const capturedItem = {
            id: captureId,
            photoUrl: imageUrl,
            timestamp: new Date()
          }

          setSessionState({
            gallery: {
              capturedItems: [...prevItems, capturedItem]
            }
          })

          // Decrement remaining captures
          remainingCapturesRef.current--

          logActivity(`Captured photo ${photoCount - remainingCapturesRef.current}/${photoCount}`, {
            itemIndex: prevItems.length,
            totalCaptured: prevItems.length + 1
          })

          // Start background filter processing for this photo
          backgroundFilterProcessor.processAllFiltersForPhoto(imageUrl, capturedItem.id)

          // Continue to next photo or complete
          if (remainingCapturesRef.current <= 0) {
            setAllCapturesComplete(true)
          } else {
            // Small delay before next photo
            setTimeout(() => {
              startCaptureSequence()
            }, 1000)
          }
        }

        return imageUrl
      }

      logActivity('Capture processing failed: No image data captured.', { level: 'error' })
      return undefined
    } catch (error) {
      console.error('Capture failed:', error)
      setIsCapturing(false)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Capture processing failed: ${errorMessage}`, { level: 'error' })
      return undefined
    }
  }

  const handleRetake = (index: number): void => {
    // Don't allow retake if currently capturing
    if (isCapturing) return

    logActivity(`Starting retake for photo ${index + 1}`)
    setRetakingIndex(index)

    // Start retake capture
    setIsCapturing(true)
    setCountdown(3)

    // --- Start video recording for retake ---
    let captureUrl: string | undefined = undefined
    const doRetake = async (): Promise<void> => {
      if (captureCanvasRecorderRef.current) {
        try {
          await captureCanvasRecorderRef.current.start()
        } catch (error) {
          console.error('Error starting capture recorder', error)
        }
      }

      // --- Step the recorder during countdown (in parallel) ---
      let frameCount = 0
      const totalFrames = FPS * 3
      const startTime = performance.now()

      // Record frames during countdown (runs in parallel with countdown)
      const recordingPromise = (async (): Promise<void> => {
        while (
          frameCount < totalFrames &&
          captureCanvasRecorderRef.current &&
          captureCanvasRecorderRef.current.status === RecorderStatus.Recording
        ) {
          const currentTime = performance.now()
          const elapsedTime = currentTime - startTime
          const targetFrameTime = (frameCount * 1000) / FPS
          if (elapsedTime >= targetFrameTime) {
            try {
              await captureCanvasRecorderRef.current.step()
              frameCount++
            } catch (error) {
              console.error('Error stepping capture recorder', error)
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 1))
        }
      })()

      const retakePromise = new Promise<string | undefined>((resolve) => {
        captureCountdownHook.setOnComplete(() => {
          handleCapture().then((url) => {
            resolve(url)
          })
        })
      })

      // Wait for both countdown and recording to complete
      const [imageUrl] = await Promise.all([retakePromise, recordingPromise])

      if (
        captureCanvasRecorderRef.current &&
        captureCanvasRecorderRef.current.status === RecorderStatus.Recording
      ) {
        try {
          const captureChunks = await captureCanvasRecorderRef.current.stop()
          const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
          captureUrl = URL.createObjectURL(captureBlob)
        } catch (error) {
          console.error('Error stopping capture recorder', error)
        }
      }

      if (imageUrl) {
        // Replace existing photo at retaking index
        const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
        const updatedItems = [...prevItems]

        if (updatedItems[index]) {
          // Clean up old photo URL
          if (updatedItems[index].photoUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(updatedItems[index].photoUrl)
          }
          // Replace with new photo and clear filteredVersions
          updatedItems[index] = {
            ...updatedItems[index],
            photoUrl: imageUrl,
            recordingUrl: captureUrl, // <-- add video
            timestamp: new Date(),
            filteredVersions: {} // Clear old filtered versions
          }
        }

        setSessionState({
          gallery: {
            capturedItems: updatedItems,
            filterCache: {} // Clear all filter caches on retake to prevent stale data
          }
        })

        logActivity(`Retook photo at index ${index}`)

        // Start background filter processing for retaken photo
        backgroundFilterProcessor.processAllFiltersForPhoto(imageUrl, updatedItems[index].id)

        setRetakingIndex(null)
        setIsCapturing(false)
      }
    }
    captureCountdownHook.setRemainingSeconds(3)
    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
    doRetake()
  }

  const handleFinish = (): void => {
    logActivity('Photostrip capture completed, proceeding to filter step')
    uploadSessionState()
    navigate(`/${projectId}/filter`)
  }

  // Generate photo placeholder grid based on selected frame
  const generatePhotoGrid = (): React.ReactElement[] => {
    const photoSlots: React.ReactElement[] = []

    // Use gallery state directly instead of local state
    const currentItems = gallery.capturedItems || []

    // Get image style for proper aspect ratio
    const getImageStyle = (): React.CSSProperties => ({
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: '0',
      left: '0'
    })

    // Container style for photo placeholders - fixed square size
    const containerStyle: React.CSSProperties = {
      position: 'relative',
      width: '250px', // Fixed square size
      height: '250px', // Fixed square size
      overflow: 'hidden',
      flexShrink: 0 // Prevent shrinking to maintain square shape
    }

    for (let i = 0; i < photoCount; i++) {
      const isCaptured = i < currentItems.length
      const currentProgress = photoCount - remainingCapturesRef.current
      const isCurrentlyCapturing = i === currentProgress && isCapturing && retakingIndex === null
      const isRetaking = retakingIndex === i && isCapturing && retakingIndex !== null
      const photoItem = isCaptured ? currentItems[i] : null

      photoSlots.push(
        <div
          key={`photo-slot-${i}-${photoItem?.id || ''}-${photoItem?.timestamp?.getTime?.() || ''}`}
          className={`relative border-3 rounded-lg overflow-hidden transition-all duration-300 ${
            isCurrentlyCapturing
              ? 'border-blue-400 bg-blue-50'
              : isCaptured
                ? 'border-green-300 bg-gray-50'
                : 'border-gray-300 bg-gray-50'
          }`}
          style={containerStyle}
        >
          {photoItem?.photoUrl ? (
            <>
              <img
                src={photoItem.photoUrl}
                alt={`Photo ${i + 1}`}
                style={getImageStyle()}
                crossOrigin="anonymous"
              />

              {/* Retaking Overlay */}
              {isRetaking && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center border-3 border-blue-400 rounded-lg">
                  <span className="text-white text-lg font-bold">Retaking...</span>
                </div>
              )}

              {/* Retake Button - Show when all captures complete and not currently capturing */}
              {allCapturesComplete && !isCapturing && !isRetaking && (
                <button
                  onClick={() => handleRetake(i)}
                  className="absolute z-30 top-2 right-2 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-colors shadow-lg"
                  aria-label={`Retake photo ${i + 1}`}
                >
                  <FaUndo className="text-black text-xl" />
                </button>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isCurrentlyCapturing ? (
                <span className="text-blue-500 text-lg font-bold">Capturing...</span>
              ) : (
                <span className="text-gray-400 text-2xl font-bold">{i + 1}</span>
              )}
            </div>
          )}
        </div>
      )
    }

    return photoSlots
  }

  // FIXED: Dynamic grid layout based on photo count - center the squares
  const getPhotoGridLayout = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'flex',
      gap: '16px',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center' // Always center the squares
    }

    switch (photoCount) {
      case 1:
        return {
          ...baseStyle,
          justifyContent: 'center',
          alignItems: 'center'
        }
      case 2:
        return {
          ...baseStyle,
          justifyContent: 'center',
          alignItems: 'center',
          gap: '24px'
        }
      case 3:
        return {
          ...baseStyle,
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px'
        }
      case 4:
        return {
          ...baseStyle,
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px'
        }
      default:
        return {
          ...baseStyle,
          justifyContent: 'center',
          alignItems: 'center'
        }
    }
  }

  // Call all hooks at the top level
  const cachedPhotostripBg = useCachedBackgroundImage(
    '/assets/boothndaughter/take-photostrips.webp'
  )

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedPhotostripBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div id="flash" className="w-full h-full flash"></div>

      {/* Countdown Display */}
      {isCapturing && (
        <div
          className="absolute"
          style={{
            top: '395px',
            right: '255px',
            fontFamily: 'monospace',
            fontSize: '160px',
            color: 'white',
            textShadow: '4px 4px 8px rgba(0,0,0,0.8)',
            fontWeight: 'bold'
          }}
        >
          {countdown}
        </div>
      )}

      {/* Preview Area */}
      <div
        className="absolute left-1/2 transform -translate-x-1/2"
        style={{
          top: '95px',
          width: '562px',
          height: '562px'
        }}
      >
        <Preview
          key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${cameraSettings.captureRatio}`}
          adjustments={selectedFilter?.adjustments}
          ratio={1} // square ratio
          cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
          filter={selectedFilter?._id}
          onCanvasReady={setCanvasDimensions}
        />
      </div>

      {/* Photo Grid - FIXED: Use flex layout to prevent overflow */}
      <div
        className="absolute bottom-8 left-8 right-8"
        style={{ height: '33vh', maxHeight: '33vh', display: 'flex', alignItems: 'center' }}
      >
        <div style={getPhotoGridLayout()}>{generatePhotoGrid()}</div>
      </div>

      {/* Progress Display */}
      <div className="absolute top-8 left-8">
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '24px',
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}
        >
          Photo{' '}
          {retakingIndex !== null
            ? retakingIndex + 1
            : Math.min(photoCount - remainingCapturesRef.current + 1, photoCount)}{' '}
          / {photoCount}
        </span>
      </div>

      {/* Timeout Display */}
      <div className="absolute top-8 right-8">
        <div className="w-20 h-20 border-4 border-red-500 rounded-full flex items-center justify-center">
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '20px',
              color: '#EF4444',
              fontWeight: 'bold'
            }}
          >
            {remainingTime}
          </span>
        </div>
      </div>

      {/* Get Ready Overlay */}
      {preparing && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-center" style={{ position: 'relative', top: '-80px' }}>
            <h1
              style={{
                fontFamily: 'monospace',
                fontSize: '64px',
                color: 'white',
                textShadow: '4px 4px 8px rgba(0,0,0,0.8)',
                fontWeight: 'bold',
                marginBottom: '24px'
              }}
            >
              Get Ready!
            </h1>
            <p
              style={{
                fontFamily: 'monospace',
                fontSize: '24px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                marginBottom: '32px'
              }}
            >
              {photoCount} photos will be taken
            </p>
            <button
              onClick={startCaptureSession}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                transition: 'transform 0.2s ease-in-out'
              }}
              className="hover:scale-105"
            >
              <img
                src="/assets/boothndaughter/start.webp"
                alt="Start Now"
                style={{
                  width: '200px',
                  height: 'auto'
                }}
              />
            </button>
          </div>
        </div>
      )}

      {/* Completion Overlay - Positioned to not block photos */}
      {allCapturesComplete && !isCapturing && (
        <div className="absolute inset-0 pointer-events-none">
          {/* Semi-transparent overlay for text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>

          {/* Text content positioned at top */}
          <div className="absolute top-72 left-1/2 transform -translate-x-1/2 text-center pointer-events-none">
            <h1
              style={{
                fontFamily: 'monospace',
                fontSize: '48px',
                color: 'white',
                textShadow: '4px 4px 8px rgba(0,0,0,0.8)',
                fontWeight: 'bold',
                marginBottom: '16px'
              }}
            >
              All Done!
            </h1>
            <p
              style={{
                fontFamily: 'monospace',
                fontSize: '18px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                marginBottom: '0'
              }}
            >
              You can retake photos or finish
            </p>
          </div>

          {/* Finish button positioned at bottom right */}
          <div className="absolute bottom-8 right-8 pointer-events-auto">
            <button
              onClick={handleFinish}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                transition: 'transform 0.2s ease-in-out'
              }}
              className="hover:scale-105"
            >
              <img
                src="/assets/boothndaughter/next.webp"
                alt="Finish"
                style={{
                  width: '150px',
                  height: 'auto'
                }}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PhotoStripCapture
